import { LimitedEditionGallery } from "./LimitedEditionGallery";
import { LimitedEditionInfoCard } from "./LimitedEditionInfoCard";
import { LimitedEditionMoodGallery } from "./LimitedEditionMoodGallery";
import styles from "./index.module.scss";

type InfoItem = {
	title?: string;
	text?: string;
	textAlignment?: string | null;
	image?: {
		url?: string;
		alt?: string | null;
		format?: string;
		width?: number;
		height?: number;
	};
};

type ShowcaseItem = {
	gallery?: Array<{
		id?: string;
		url?: string;
		alt?: string | null;
		responsiveImage?: unknown;
	}>;
	info?: InfoItem[];
	hideGallery?: boolean;
	hasMoodGallery?: boolean;
	moodGallery?: Array<{
		id?: string;
		url?: string;
		alt?: string | null;
		responsiveImage?: unknown;
	}>;
};

type LimitedEditionShowcaseRowProps = {
	item: ShowcaseItem;
	inverted?: boolean;
	sectionBgHex?: string;
	cardWhiteHex?: string;
	textColor?: { color?: { hex?: string; cssRgb?: string } };
	iconColor?: { color?: { hex?: string } };
};

const CARD_WHITE = "#ffffff";

export function LimitedEditionShowcaseRow({
	item,
	inverted = false,
	sectionBgHex,
	cardWhiteHex = CARD_WHITE,
	textColor,
	iconColor,
}: LimitedEditionShowcaseRowProps) {
	const gallery = item?.gallery ?? [];
	const info = item?.info ?? [];
	const hideGallery = Boolean(item?.hideGallery);
	const moodGallery = item?.moodGallery ?? [];
	const showMoodGallery = item?.hasMoodGallery && moodGallery.length > 0;

	if (
		(!hideGallery && !gallery.length && !info.length && !showMoodGallery) ||
		(hideGallery && !info.length && !showMoodGallery)
	) {
		return null;
	}

	const firstRow = info.slice(0, 3);
	const secondRow = info.slice(3);

	// Normal: section bg behind, white cards. Inverted: swap the two.
	const rowBg = inverted ? cardWhiteHex : "transparent";
	const cardBg = inverted ? sectionBgHex ?? cardWhiteHex : cardWhiteHex;

	return (
		<div
			className={styles.row}
			style={{ backgroundColor: rowBg }}
		>
			<div className="container mx-auto">
				<div
					className={`${styles.layout} ${
						hideGallery
							? styles.layoutHideGallery
							: inverted
								? styles.layoutReverse
								: ""
					}`}
				>
					{!hideGallery && (
						<div className={styles.galleryCol}>
							<LimitedEditionGallery
								images={gallery}
								dotColor={iconColor?.color?.hex}
							/>
						</div>
					)}
					<div
						className={`${styles.info} ${styles.infoCol} ${
							hideGallery ? styles.infoColCentered : ""
						}`}
					>
						{firstRow.length > 0 && (
							<div className={styles.infoRowThirds}>
								{firstRow.map((entry, index) => (
									<LimitedEditionInfoCard
										key={`${entry.title}-${index}`}
										item={entry}
										textColor={textColor?.color}
										iconColor={iconColor?.color}
										cardBgColor={cardBg}
									/>
								))}
							</div>
						)}
						{secondRow.length > 0 && (
							<div className={styles.infoRowHalves}>
								{secondRow.map((entry, index) => (
									<LimitedEditionInfoCard
										key={`${entry.title}-${index}`}
										item={entry}
										textColor={textColor?.color}
										iconColor={iconColor?.color}
										cardBgColor={cardBg}
									/>
								))}
							</div>
						)}
					</div>
				</div>
				{showMoodGallery && (
					<LimitedEditionMoodGallery images={moodGallery} />
				)}
			</div>
		</div>
	);
}
